import React from "react";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { theme } from "../../../v2/design-system/theme";

const bp = theme.breakpoints || { sm: "640px" };

/** Outer shell for org hub tab panels (replaces MUI Paper/Box stacks). */
export const HubPageShell = styled.div`
  padding: ${theme.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
`;

export const HubFlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(p) => p.$justify ?? "flex-start"};
  flex-wrap: wrap;
  gap: ${(p) => p.$gap ?? theme.spacing.md};
  margin-bottom: ${(p) => p.$mb ?? 0};
  margin-top: ${(p) => p.$mt ?? 0};
`;

export const HubTitleXl = styled.h2`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  line-height: ${theme.typography.lineHeight.tight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const HubTitleLg = styled.h2`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

export const HubMutedText = styled.p`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin: ${theme.spacing.xs} 0 0;
`;

export const HubFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: ${theme.spacing.md};
  @media (max-width: ${bp.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const HubFieldCell = styled.div`
  grid-column: span ${(p) => p.$span ?? 12};
  @media (max-width: ${bp.sm}) {
    grid-column: span 1;
  }
`;

export const HubTableOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 10;
`;

const HubNarrationRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  max-width: 220px;
  cursor: ${(p) => (p.$hoverable ? "pointer" : "default")};
`;

const HubNarrationText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1 1 auto;
`;

const HubNarrationHintIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${theme.colors.textMuted};
  line-height: 0;

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const narrationTooltipSx = {
  maxWidth: 400,
  fontSize: theme.typography.fontSize.sm,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeight.normal,
  lineHeight: theme.typography.lineHeight.relaxed,
  bgcolor: theme.colors.primary,
  color: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
  py: 1,
  px: 1.25,
};

/** Truncated narration: MUI hover tooltip for full text + info icon (no native `title` / question-mark cursor). */
export function HubNarrationCell({ fullText, limit = 12 }) {
  const text = fullText == null || fullText === "" ? "-" : String(fullText);
  const isLong = text.length > limit;
  const display = isLong ? `${text.slice(0, limit)}…` : text;

  const row = (
    <HubNarrationRow $hoverable={isLong}>
      <HubNarrationText>{display}</HubNarrationText>
      {isLong && (
        <HubNarrationHintIcon aria-hidden>
          <svg viewBox="0 0 24 24" fill="currentColor" focusable="false" aria-hidden>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
        </HubNarrationHintIcon>
      )}
    </HubNarrationRow>
  );

  if (!isLong) return row;

  return (
    <Tooltip
      title={text}
      placement="top"
      arrow
      enterDelay={150}
      leaveDelay={0}
      describeChild
      componentsProps={{
        tooltip: { sx: narrationTooltipSx },
        arrow: { sx: { color: theme.colors.primary } },
      }}
    >
      {row}
    </Tooltip>
  );
}
